/**
 * Relationship Styles Configuration
 * 
 * This file defines the visual styling for different relationship types in the service map.
 * It includes categories, colors, line styles, and other visual properties.
 */

// Define relationship categories with their visual properties
export const RELATIONSHIP_CATEGORIES = {
  coreDependencies: {
    name: 'Core Dependencies',
    lineStyle: 'solid',
    lineWidth: 2,
    description: 'Solid line'
  },
  communication: {
    name: 'Communication & Data Flow',
    lineStyle: 'dashed',
    lineWidth: 2,
    description: 'Dashed line'
  },
  dataManagement: {
    name: 'Data Management',
    lineStyle: 'dotted',
    lineWidth: 2,
    description: 'Dotted line'
  },
  physical: {
    name: 'Physical Infrastructure',
    lineStyle: 'solid',
    lineWidth: 3,
    description: 'Thick solid line'
  },
  control: {
    name: 'Management & Control',
    lineStyle: 'solid',
    lineWidth: 3,
    description: 'Thick solid line'
  },
  structural: {
    name: 'Structural & Organization',
    lineStyle: 'solid',
    lineWidth: 3,
    description: 'Thick solid line'
  },
  resourceManagement: {
    name: 'Resource Management',
    lineStyle: 'dash-dot',
    lineWidth: 2,
    description: 'Dash-dot line'
  },
  virtualization: {
    name: 'Virtualization & Clustering',
    lineStyle: 'dotted',
    lineWidth: 2,
    description: 'Dotted line'
  },
  reliability: {
    name: 'Disaster Recovery',
    lineStyle: 'dashed',
    lineWidth: 3,
    description: 'Thick dashed line'
  },
  integration: {
    name: 'Endpoint & Integration',
    lineStyle: 'dash-dot',
    lineWidth: 2,
    description: 'Dash-dot line'
  },
  discovery: {
    name: 'Registration & Discovery',
    lineStyle: 'dotted',
    lineWidth: 2,
    description: 'Dotted line'
  }
};

// Define relationship type style interface
export interface RelationshipTypeStyle {
  type: string;
  category: keyof typeof RELATIONSHIP_CATEGORIES;
  color: string;
  abbreviation: string;
  fullLabel: string;
}

// Define specific relationship types with their categories and colors (all 44 ServiceNow types)
export const RELATIONSHIP_TYPE_STYLES: Record<string, RelationshipTypeStyle> = {
  // Core Dependencies (Solid lines)
  'depends_on': {
    type: 'depends_on',
    category: 'coreDependencies',
    color: '#1976D2',
    abbreviation: 'dep',
    fullLabel: 'Depends On'
  },
  'hosted_on': {
    type: 'hosted_on',
    category: 'coreDependencies',
    color: '#00796B',
    abbreviation: 'host',
    fullLabel: 'Hosted On'
  },
  'runs_on': {
    type: 'runs_on',
    category: 'coreDependencies',
    color: '#5D4037',
    abbreviation: 'runs',
    fullLabel: 'Runs On'
  },
  'uses': {
    type: 'uses',
    category: 'coreDependencies',
    color: '#388E3C',
    abbreviation: 'uses',
    fullLabel: 'Uses'
  },
  'used_by': {
    type: 'used_by',
    category: 'coreDependencies',
    color: '#2E7D32',
    abbreviation: 'used',
    fullLabel: 'Used By'
  },
  'provides': {
    type: 'provides',
    category: 'coreDependencies',
    color: '#2E7D32',
    abbreviation: 'prov',
    fullLabel: 'Provides'
  },
  'provided_by': {
    type: 'provided_by',
    category: 'coreDependencies',
    color: '#4CAF50',
    abbreviation: 'prov_by',
    fullLabel: 'Provided By'
  },
  'consumes': {
    type: 'consumes',
    category: 'coreDependencies',
    color: '#C62828',
    abbreviation: 'cons',
    fullLabel: 'Consumes'
  },
  'consumed_by': {
    type: 'consumed_by',
    category: 'coreDependencies',
    color: '#D32F2F',
    abbreviation: 'cons_by',
    fullLabel: 'Consumed By'
  },

  // Communication & Data Flow (Dashed lines)
  'connects_to': {
    type: 'connects_to',
    category: 'communication',
    color: '#F57C00',
    abbreviation: 'conn',
    fullLabel: 'Connects To'
  },
  'connected_by': {
    type: 'connected_by',
    category: 'communication',
    color: '#FF9800',
    abbreviation: 'conn_by',
    fullLabel: 'Connected By'
  },
  'ip_connection': {
    type: 'ip_connection',
    category: 'communication',
    color: '#FF5722',
    abbreviation: 'ip',
    fullLabel: 'IP Connection'
  },
  'exchanges_data_with': {
    type: 'exchanges_data_with',
    category: 'communication',
    color: '#1565C0',
    abbreviation: 'exch',
    fullLabel: 'Exchanges Data With'
  },
  'receives_data_from': {
    type: 'receives_data_from',
    category: 'communication',
    color: '#C62828',
    abbreviation: 'recv',
    fullLabel: 'Receives Data From'
  },
  'exports_to': {
    type: 'exports_to',
    category: 'communication',
    color: '#2E7D32',
    abbreviation: 'exp',
    fullLabel: 'Exports To'
  },
  'applicative_flow_to': {
    type: 'applicative_flow_to',
    category: 'communication',
    color: '#7B1FA2',
    abbreviation: 'app_flow',
    fullLabel: 'Applicative Flow To'
  },

  // Data Management (Dotted lines)
  'provides_storage_for': {
    type: 'provides_storage_for',
    category: 'dataManagement',
    color: '#2E7D32',
    abbreviation: 'store',
    fullLabel: 'Provides Storage For'
  },
  'distributed_by': {
    type: 'distributed_by',
    category: 'dataManagement',
    color: '#388E3C',
    abbreviation: 'dist',
    fullLabel: 'Distributed By'
  },

  // Physical Infrastructure (Thick solid lines)
  'powers': {
    type: 'powers',
    category: 'physical',
    color: '#FFC107',
    abbreviation: 'power',
    fullLabel: 'Powers'
  },
  'cools': {
    type: 'cools',
    category: 'physical',
    color: '#00BCD4',
    abbreviation: 'cool',
    fullLabel: 'Cools'
  },
  'feeds': {
    type: 'feeds',
    category: 'physical',
    color: '#4CAF50',
    abbreviation: 'feed',
    fullLabel: 'Feeds'
  },
  'in_rack': {
    type: 'in_rack',
    category: 'physical',
    color: '#795548',
    abbreviation: 'rack',
    fullLabel: 'In Rack'
  },
  'located_in': {
    type: 'located_in',
    category: 'physical',
    color: '#607D8B',
    abbreviation: 'loc',
    fullLabel: 'Located In'
  },
  'located_in_zone': {
    type: 'located_in_zone',
    category: 'physical',
    color: '#9E9E9E',
    abbreviation: 'zone',
    fullLabel: 'Located In Zone'
  },

  // Management & Control (Thick solid lines)
  'manages': {
    type: 'manages',
    category: 'control',
    color: '#9C27B0',
    abbreviation: 'mgr',
    fullLabel: 'Manages'
  },
  'managed_by': {
    type: 'managed_by',
    category: 'control',
    color: '#673AB7',
    abbreviation: 'mgr_by',
    fullLabel: 'Managed By'
  },
  'controller_for': {
    type: 'controller_for',
    category: 'control',
    color: '#AD1457',
    abbreviation: 'ctrl',
    fullLabel: 'Controller For'
  },
  'owns': {
    type: 'owns',
    category: 'control',
    color: '#E91E63',
    abbreviation: 'owns',
    fullLabel: 'Owns'
  },
  'impacts': {
    type: 'impacts',
    category: 'control',
    color: '#F44336',
    abbreviation: 'impact',
    fullLabel: 'Impacts'
  },
  'discovers': {
    type: 'discovers',
    category: 'control',
    color: '#3F51B5',
    abbreviation: 'disc',
    fullLabel: 'Discovers'
  },

  // Structural & Organization (Thick solid lines)
  'members': {
    type: 'members',
    category: 'structural',
    color: '#9C27B0',
    abbreviation: 'member',
    fullLabel: 'Members'
  },
  'master_of': {
    type: 'master_of',
    category: 'structural',
    color: '#FF9800',
    abbreviation: 'master',
    fullLabel: 'Master Of'
  },
  'cluster_of': {
    type: 'cluster_of',
    category: 'structural',
    color: '#FF5722',
    abbreviation: 'cluster',
    fullLabel: 'Cluster Of'
  },
  'contains': {
    type: 'contains',
    category: 'structural',
    color: '#7B1FA2',
    abbreviation: 'contains',
    fullLabel: 'Contains'
  },

  // Resource Management (Dash-dot lines)
  'allocated_from': {
    type: 'allocated_from',
    category: 'resourceManagement',
    color: '#795548',
    abbreviation: 'alloc_from',
    fullLabel: 'Allocated From'
  },
  'allocated_to': {
    type: 'allocated_to',
    category: 'resourceManagement',
    color: '#5D4037',
    abbreviation: 'alloc_to',
    fullLabel: 'Allocated To'
  },
  'defines_resources_for': {
    type: 'defines_resources_for',
    category: 'resourceManagement',
    color: '#8D6E63',
    abbreviation: 'def_res',
    fullLabel: 'Defines Resources For'
  },
  'provisioned_from': {
    type: 'provisioned_from',
    category: 'resourceManagement',
    color: '#A1887F',
    abbreviation: 'prov_from',
    fullLabel: 'Provisioned From'
  },

  // Virtualization & Clustering (Dotted lines)
  'virtualized_by': {
    type: 'virtualized_by',
    category: 'virtualization',
    color: '#2196F3',
    abbreviation: 'virt',
    fullLabel: 'Virtualized By'
  },
  'instantiates': {
    type: 'instantiates',
    category: 'virtualization',
    color: '#03A9F4',
    abbreviation: 'inst',
    fullLabel: 'Instantiates'
  },
  'from_template': {
    type: 'from_template',
    category: 'virtualization',
    color: '#00BCD4',
    abbreviation: 'template',
    fullLabel: 'From Template'
  },

  // Disaster Recovery (Thick dashed lines)
  'dr_provided_by': {
    type: 'dr_provided_by',
    category: 'reliability',
    color: '#D84315',
    abbreviation: 'dr',
    fullLabel: 'DR Provided By'
  },

  // Endpoint & Integration (Dash-dot lines)
  'implement_end_point_to': {
    type: 'implement_end_point_to',
    category: 'integration',
    color: '#9C27B0',
    abbreviation: 'impl_ep',
    fullLabel: 'Implement End Point To'
  },
  'use_end_point_to': {
    type: 'use_end_point_to',
    category: 'integration',
    color: '#673AB7',
    abbreviation: 'use_ep',
    fullLabel: 'Use End Point To'
  },

  // Registration & Discovery (Dotted lines)
  'registered_on': {
    type: 'registered_on',
    category: 'discovery',
    color: '#607D8B',
    abbreviation: 'reg',
    fullLabel: 'Registered On'
  }
};

/**
 * Get the style information for a relationship type
 */
export function getRelationshipStyle(relationshipType: string): RelationshipTypeStyle {
  return RELATIONSHIP_TYPE_STYLES[relationshipType] || {
    type: relationshipType,
    category: 'coreDependencies',
    color: '#666666',
    abbreviation: relationshipType.substring(0, 4),
    fullLabel: relationshipType
  };
}

/**
 * Get the category information for a relationship type
 */
export function getRelationshipCategory(relationshipType: string) {
  const style = getRelationshipStyle(relationshipType);
  return RELATIONSHIP_CATEGORIES[style.category];
}

/**
 * Get all relationship types for a specific category
 */
export function getRelationshipTypesByCategory(category: keyof typeof RELATIONSHIP_CATEGORIES): string[] {
  return Object.keys(RELATIONSHIP_TYPE_STYLES).filter(
    type => RELATIONSHIP_TYPE_STYLES[type].category === category
  );
}

/**
 * Get all available relationship types
 */
export function getAllRelationshipTypes(): string[] {
  return Object.keys(RELATIONSHIP_TYPE_STYLES);
}

/**
 * Get all available categories
 */
export function getAllCategories(): (keyof typeof RELATIONSHIP_CATEGORIES)[] {
  return Object.keys(RELATIONSHIP_CATEGORIES) as (keyof typeof RELATIONSHIP_CATEGORIES)[];
}

/**
 * Get line style information for a relationship type
 */
export function getRelationshipLineStyle(relationshipType: string) {
  const style = getRelationshipStyle(relationshipType);
  const category = RELATIONSHIP_CATEGORIES[style.category];
  
  // Convert line style to strokeDasharray
  let strokeDasharray = '0'; // solid line
  
  switch (category.lineStyle) {
    case 'dashed':
      strokeDasharray = '8,4';
      break;
    case 'dotted':
      strokeDasharray = '2,3';
      break;
    case 'dash-dot':
      strokeDasharray = '8,4,2,4';
      break;
    case 'solid':
    default:
      strokeDasharray = '0';
      break;
  }
  
  return {
    strokeWidth: category.lineWidth,
    strokeDasharray: strokeDasharray
  };
}
